import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Menu, { MappedLink } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomArrowDown from "@tSurya/Icons/CustomArrowDown";
import GVUser from "@tSurya/Icons/GVUser";
import MGBWChevronDownIcon from "@tSurya/Icons/MGBWChevronDownIcon";
import MGBWUserIcon from "@tSurya/Icons/MGBWUserIcon";
import User from "@tSurya/Icons/User";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import { getActiveThemeId, isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import { Property } from "csstype";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
    showInventory: state.context.showInventory,
    showPricing: state.context.showPricing,
    isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
    hasChangeLastQty:
        state.pages.vmiBins.selectVmiBinsChangedQty &&
        Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
    orderConfirmationPageUrl: getPageLinkByPageType(state, "OrderConfirmationPage")?.url,
    onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
    session: state.context.session,
    userRole: state.context.session.userRoles,
    isSuryaInternalUser: state.context.session.properties?.isSuryaInternalUser,
    billToState: getBillToState(state, state.context.session.billToId),
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setNavDrawerIsOpen,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    loadBillTo,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    signInWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    DownIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signInTitleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    accountWrapper?: InjectableCss;
    mgbwChevronWrapper?: InjectableCss;
    clickableWrapper?: InjectableCss;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            padding: 0
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 0px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }};
        `,
    },
    signInWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            padding: ${isThemeActive(THEME_IDS.GLOBAL_VIEWS) || isThemeActive(THEME_IDS.MGBW) ? '0' : '6px 0'};
            @media only screen and (max-width: 1166px) {
                padding: 0px;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 0px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    titleClickable: {
        css: css`
            height: ${TYPOGRAPHY.header.lineHeight};
        `,
    },
    titleIcon: {
        size: isThemeActive(THEME_IDS.GLOBAL_VIEWS) || isThemeActive(THEME_IDS.MGBW) ? 20 : 24,
        css: css`
            padding-top: 0;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-top: 0px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    DownIcon: {
        size: 10,
        css: css`
            margin-left: 8px;
            ${isThemeActive(THEME_IDS.GLOBAL_VIEWS) || isThemeActive(THEME_IDS.MGBW) ? 
                `height: 24px !important` : 
                ``
            }
        `,
    },
    titleTypography: {
        css: css`
            margin-left: ${isThemeActive(THEME_IDS.MGBW) ? '8px' : '4px'};
            max-width: 150px;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.signInTitle.weight};
            font-size: ${TYPOGRAPHY.header.size};
            line-height: ${TYPOGRAPHY.header.lineHeight};
            /* identical to box height */
            text-transform: ${TYPOGRAPHY.header.textTransform};
            color: ${getColor("text.main")};
        `,
        ellipsis: true,
        transform: TYPOGRAPHY.header.textTransform as Property.TextTransform,
    },
    signInTitleTypography: {
        css: css`
            padding-top: 0;
            margin-left: ${isThemeActive(THEME_IDS.MGBW) ? '8px' : '4px'};
            max-width: 150px;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-size: ${TYPOGRAPHY.header.size};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.signInTitle.weight};
            line-height: ${TYPOGRAPHY.header.lineHeight};
            /* identical to box height */
            text-transform: ${TYPOGRAPHY.header.textTransform};
            color: ${getColor("text.main")};
        `,
        ellipsis: true,
        transform: TYPOGRAPHY.header.textTransform as Property.TextTransform,
    },
    signOutClickable: {
        css: css`
            width: 100%;
            padding: 10px 15px 10px 20px;
            min-height: 42px;
            font-style: normal;
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
            color: ${isThemeActive(THEME_IDS.MGBW) ? COLORS.text.main : COLORS.darkBlack} !important;
            font-size: ${isThemeActive(THEME_IDS.MGBW) ? TYPOGRAPHY.size.small : TYPOGRAPHY.size.smaller} !important;
            font-weight: ${TYPOGRAPHY.weight.baseBold} !important;
            line-height: ${isThemeActive(THEME_IDS.MGBW) ? '1.5' : '22px'} !important;
            letter-spacing: ${isThemeActive(THEME_IDS.MGBW) ? '0.08px' : 'normal'} !important;
            text-transform: capitalize !important;
            &:hover {
                background-color: rgb(204, 204, 204);
            }
        `,
    },
    accountWrapper: {
        css: css`
            display: flex;
            ${isThemeActive(THEME_IDS.GLOBAL_VIEWS)
                ? `align-items: flex-start` 
                : `align-items: center;`
            }
        `
    },
    mgbwChevronWrapper: {
        css: css`
            margin-left: 6px;
            display: flex;
            align-items: center;
        `,
    },
    clickableWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;
export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    session,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    userRole,
    isSuryaInternalUser,
    setSignInOverlayIsOpen,
    theme,
    setShowInventory,
    setShowPricing,
    setNavDrawerIsOpen,
    billToState,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    loadBillTo,
    orderConfirmationPageUrl,
    onePageCheckoutConfirmationPageUrl,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    const accountDisplayName = [session.firstName, session.lastName].filter(Boolean).join(" ").trim() || userName;

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    // Load billTo data if not already loaded and not currently loading
    useEffect(() => {
        if (session.billToId && !billToState?.value && !billToState?.isLoading) {
            loadBillTo({ billToId: session.billToId });
        }
    }, [session.billToId, billToState?.value, billToState?.isLoading, loadBillTo]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;

    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    const getUserIcon = () => {
        const activeThemeId = getActiveThemeId();

        switch (activeThemeId) {
            case THEME_IDS.GLOBAL_VIEWS:
                return GVUser;
            case THEME_IDS.MGBW:
                return MGBWUserIcon;
            default:
                return User;
        }
    };

    const userIcon = getUserIcon();

    if (fields.icon === "User") {
        icon = userIcon;
    }

    const currentMode = getCookie(NavigationModeCookieName);

    const excludeLinksMyAccount: any = siteMessage("Exclude_Links_MyAccount");
    let excludeLinksMyAccountArr: string[] = [];

    if (typeof excludeLinksMyAccount === "string" && excludeLinksMyAccount.trim() !== "") {
        excludeLinksMyAccountArr = excludeLinksMyAccount.split("|");
    }

    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink =>
            !(
                pageLink.type === "ChangeCustomerPage" &&
                (!displayChangeCustomerLink || session.properties?.hasMultipleCustomers === "False")
            ) &&
            !(pageLink.title === "Transactions" && userRole !== "Administrator" && userRole !== "Buyer3") &&
            !(pageLink.title === "Manage My Users" && isSuryaInternalUser !== "true") &&
            !excludeLinksMyAccountArr.includes(pageLink.title) &&
            !(
                (pageLink.title === "Order Status" ||
                    pageLink.title === "Upload Order" ||
                    pageLink.title === "Settings") &&
                userRole === "Collaborator"
            ),
    ) ?? []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: ${TYPOGRAPHY.signInTitle.textTransform};
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");
        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
                return;
            }
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (
                returnUrl === "/" ||
                (currentUserIsGuest && orderConfirmationPageUrl && returnUrl.indexOf(orderConfirmationPageUrl) > -1) ||
                (currentUserIsGuest &&
                    onePageCheckoutConfirmationPageUrl &&
                    returnUrl.indexOf(onePageCheckoutConfirmationPageUrl) > -1)
            ) {
                history.push(`${signInUrl}`);
                return;
            }

            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    const salesPerson = session.isSalesPerson;

    return (
        <StyledWrapper {...(!session.isAuthenticated ? styles.signOutWrapper : styles.signInWrapper)}>
            {includeAccountMenu && userName && !currentUserIsGuest && (
                <Menu
                    descriptionId="accountMenu"
                    menuItems={menuItems}
                    displayChangeCustomerLink={displayChangeCustomerLink}
                    maxDepth={1}
                    menuTrigger={
                        <StyledWrapper {...styles.accountWrapper}>
                            {showIcon && <Icon {...styles.titleIcon} src={icon} title={userName} />}
                            <StyledWrapper {...styles.clickableWrapper}>
                                <Clickable
                                    {...styles.titleClickable}
                                    data-test-selector="header_signIn"
                                    aria-label="header_userName"
                                >
                                    {showLabel && (
                                        <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                            {accountDisplayName}
                                        </Typography>
                                    )}
                                </Clickable>
                                {billToState?.value?.customerNumber &&
                                    billToState?.value?.customerNumber !== "0" &&
                                    (session?.properties?.hasMultipleCustomers === "True" || salesPerson) && (
                                        <>
                                            <Typography
                                                as="p"
                                                data-test-selector="header_userName"
                                                style={{ 
                                                    marginLeft: isThemeActive(THEME_IDS.MGBW) ? "8px" : "4px",
                                                    marginTop: "0", 
                                                    fontSize: isThemeActive(THEME_IDS.GLOBAL_VIEWS) || isThemeActive(THEME_IDS.MGBW) ? "14px" : "12px", 
                                                    position: "relative" 
                                                }}
                                            >
                                                AS{" "}
                                                <span style={{ fontWeight: "bolder" }}>
                                                    {billToState?.value?.customerNumber}{" "}
                                                </span>
                                            </Typography>
                                        </>
                                    )}
                            </StyledWrapper>
                            {isThemeActive(THEME_IDS.MGBW) ? (
                                <StyledWrapper {...styles.mgbwChevronWrapper}>
                                    <MGBWChevronDownIcon />
                                </StyledWrapper>
                            ) : (
                                <Icon {...styles.DownIcon} src={CustomArrowDown} />
                            )}
                        </StyledWrapper>
                    }
                    cssOverrides={{
                        wrapper: css`
                            flex: 0 1 auto;
                            min-width: 0;
                            > ul {
                                max-height: 450px;
                                overflow-y: auto;
                                a {
                                    color: ${isThemeActive(THEME_IDS.MGBW) ? COLORS.text.main : COLORS.darkBlack} !important;
                                    font-family: ${TYPOGRAPHY.fontFamily.base} !important;
                                    font-size: ${isThemeActive(THEME_IDS.MGBW) ? TYPOGRAPHY.size.small : TYPOGRAPHY.size.smaller} !important;
                                    font-weight: ${TYPOGRAPHY.weight.baseBold} !important;
                                    line-height: ${isThemeActive(THEME_IDS.MGBW) ? '1.5' : '22px'} !important;
                                    letter-spacing: ${isThemeActive(THEME_IDS.MGBW) ? '0.08px' : 'normal'} !important;
                                    text-transform: capitalize !important;
                                }
                                span {
                                    color: ${isThemeActive(THEME_IDS.MGBW) ? COLORS.text.main : COLORS.darkBlack} !important;
                                    font-family: ${TYPOGRAPHY.fontFamily.base} !important;
                                    font-size: ${isThemeActive(THEME_IDS.MGBW) ? TYPOGRAPHY.size.small : TYPOGRAPHY.size.smaller} !important;
                                    font-weight: ${TYPOGRAPHY.weight.baseBold} !important;
                                    line-height: ${isThemeActive(THEME_IDS.MGBW) ? '1.5' : '22px'} !important;
                                    letter-spacing: ${isThemeActive(THEME_IDS.MGBW) ? '0.08px' : 'normal'} !important;
                                    text-transform: capitalize !important;
                                }
                            }
                        `,
                    }}
                    data-test-selector="header_accountMenu"
                >
                    {userName && !currentUserIsGuest && (
                        <Clickable
                            {...styles.signOutClickable}
                            onClick={onSignOutHandler}
                            data-test-selector="headerSignOutLink"
                        >
                            {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                        </Clickable>
                    )}
                </Menu>
            )}
            {(!includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                <Clickable
                    {...styles.titleClickable}
                    onClick={onSignInHandler}
                    href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                    data-test-selector="header_signIn"
                    aria-label="header_signIn"
                >
                    {showIcon && <Icon {...styles.titleIcon} src={icon} title={signInStatusText} />}
                    {showLabel && <Typography {...styles.signInTitleTypography}>{signInStatusText}</Typography>}
                </Clickable>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;

